import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Check, Ticket } from "lucide-react";

export default function AdminCodes() {
  const [codes, setCodes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({ package_id: "", quantity: 1, label: "" });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // all | unused | used
  const [copied, setCopied] = useState(null);

  const reload = () => api.get("/admin/codes").then(r => setCodes(r.data));
  useEffect(() => {
    reload();
    api.get("/packages").then(r => { setPackages(r.data); if (r.data[0]) setForm(f => ({ ...f, package_id: r.data[0].id })); });
  }, []);

  const create = async () => {
    if (!form.package_id) { toast.error("Pick a package"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/codes", { ...form, quantity: Number(form.quantity) });
      toast.success(`${data.length} code${data.length > 1 ? "s" : ""} generated`);
      reload();
    } catch (e) { toast.error("Could not generate codes"); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this code? (only unused codes can be deleted)")) return;
    try { await api.delete(`/admin/codes/${id}`); toast.success("Code deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot delete used code"); }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      toast.success(`Copied: ${code}`);
      setTimeout(() => setCopied(null), 1500);
    } catch { toast.error("Copy failed"); }
  };

  const filtered = codes.filter(c => {
    if (filter === "unused") return !c.used_by;
    if (filter === "used") return !!c.used_by;
    return true;
  });
  const unusedCount = codes.filter(c => !c.used_by).length;

  return (
    <div data-testid="admin-codes">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">ACCESS CODES</div>
      <h1 className="font-display text-4xl uppercase">Free <span className="text-[#d7ff00]">access codes</span></h1>
      <p className="mt-2 text-sm text-[#888] max-w-2xl">Generate single-use codes to give runners a free program. Each code redeems exactly once — when a runner uses it, it locks to them, so they can't share with friends.</p>

      {/* Generator */}
      <div className="mt-6 bg-[#0a0a0a] border border-[#1f1f1f] p-5">
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] mb-3">GENERATE NEW CODES</div>
        <div className="grid sm:grid-cols-[1fr_120px_1fr_auto] gap-3 items-end">
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Package</label>
            <select value={form.package_id} onChange={e => setForm({ ...form, package_id: e.target.value })} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2" data-testid="code-package">
              {packages.map(p => <option key={p.id} value={p.id}>{p.name} · {p.currency} {p.price}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Quantity</label>
            <input type="number" min={1} max={200} value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2" data-testid="code-qty" />
          </div>
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Label (optional)</label>
            <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. KL Run Club 2026" className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2" data-testid="code-label" />
          </div>
          <button onClick={create} disabled={busy} className="bg-[#d7ff00] text-black font-display uppercase tracking-wider px-5 py-2.5 hover:bg-white disabled:opacity-50 flex items-center gap-2" data-testid="code-create">
            <Plus className="w-4 h-4" /> {busy ? "..." : "Generate"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-2 items-center">
        <Chip active={filter === "all"} onClick={() => setFilter("all")} testid="codes-all">All · {codes.length}</Chip>
        <Chip active={filter === "unused"} onClick={() => setFilter("unused")} testid="codes-unused">Available · {unusedCount}</Chip>
        <Chip active={filter === "used"} onClick={() => setFilter("used")} testid="codes-used">Redeemed · {codes.length - unusedCount}</Chip>
      </div>

      {/* List */}
      <div className="mt-3 bg-[#0a0a0a] border border-[#1f1f1f] divide-y divide-[#1f1f1f]">
        {filtered.length === 0 && <div className="p-5 text-[#888] text-sm">No codes match this filter.</div>}
        {filtered.map(c => (
          <div key={c.id} className="p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`code-${c.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <Ticket className={`w-5 h-5 flex-shrink-0 ${c.used_by ? "text-[#666]" : "text-[#d7ff00]"}`} />
              <div className="min-w-0">
                <div className="font-mono-jp text-base sm:text-lg tracking-widest" style={{ textDecoration: c.used_by ? "line-through" : "none", color: c.used_by ? "#666" : "#fff" }}>{c.code}</div>
                <div className="font-mono-jp text-[10px] text-[#888] uppercase tracking-widest">
                  {c.package_name}
                  {c.label && <> · {c.label}</>}
                  {c.used_by && <> · USED BY {c.used_by_name || c.used_by_email}</>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {c.used_by ? (
                <span className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666] border border-[#333] px-2 py-1">REDEEMED</span>
              ) : (
                <>
                  <button onClick={() => copy(c.code)} className="bg-[#111] border border-[#222] hover:border-[#d7ff00] px-3 py-1.5 text-xs font-mono-jp uppercase tracking-widest flex items-center gap-1" data-testid={`copy-${c.id}`}>
                    {copied === c.code ? <Check className="w-3 h-3 text-[#d7ff00]" /> : <Copy className="w-3 h-3" />} {copied === c.code ? "Copied" : "Copy"}
                  </button>
                  <button onClick={() => remove(c.id)} className="text-[#ff3b30] px-2" data-testid={`delete-code-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Chip({ active, onClick, testid, children }) {
  const cls = active ? "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/10" : "border-[#222] text-[#bbb] hover:border-[#d7ff00] hover:text-white";
  return <button onClick={onClick} className={`inline-flex items-center gap-1 px-3 py-2 text-xs font-mono-jp uppercase tracking-widest border ${cls}`} data-testid={testid}>{children}</button>;
}
